/**
 * Message slice — Messagerie narrative SCD.
 *
 * Holds:
 *   - inbox     : array of message instances (most recent first)
 *   - drafts    : { msgId → choiceId } selections in progress (optional)
 *   - lastRandomDay : last day a random message was rolled (cap 1/day)
 *
 * Each message in the inbox has the shape produced by `_instantiateTemplate`
 * below — see /app/frontend/src/data/messageTemplates.js for the contract.
 *
 * Effects supported by `applyMessageChoice`:
 *   { kind: 'treasury',           amount }
 *   { kind: 'reputation',         amount }
 *   { kind: 'notoriety',          amount }
 *   { kind: 'driverRaise',        amount }  // applies to message.driverId
 *   { kind: 'driverSatisfaction', amount }
 *   { kind: 'driverFire',         amount: ignored }
 *   { kind: 'creditScore',        amount }
 *   { kind: 'creditOffer',        amount, months, rate }  // builds a credit
 */

import { COMPETITORS } from '../../data/competitors';
import { MESSAGE_TEMPLATES, pickRandomTemplate } from '../../data/messageTemplates';
import { applyRetailEventEffect } from '../../utils/retailMarketing';
import { CLIENT_BY_ID, getLoyaltyTier } from '../../data/clientLoyalty';
import { getInterimDailyCost } from '../helpers/driverHolidayTick';
import { pickThanksAfterRaiseTemplate } from '../helpers/thanksAfterRaisePicker';
import { isCorruptedMessageBody } from '../helpers/inboxIntegrity';

const MAX_INBOX = 80;

const generateId = () => `msg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

/**
 * Backfill convenience aliases (`driverName`, `driverFirstName`, …) from
 * legacy short-form keys (`name`) that some internal callers still use.
 * This keeps `_instantiateTemplate` strict about ghost-placeholder detection
 * while not breaking the dozen-or-so call sites that pass `{ driverId, name }`.
 */
const _normaliseContext = (ctx) => {
  const out = { ...ctx };
  // Driver aliases.
  if (out.driverName == null && out.name != null && out.driverId != null) {
    out.driverName = out.name;
  }
  if (out.driverFirstName == null && out.driverName != null) {
    out.driverFirstName = String(out.driverName).split(' ')[0] || out.driverName;
  }
  // Client aliases (some callers pass `clientHolder` or `name` for a client).
  if (out.clientName == null && out.client != null) {
    out.clientName = out.client;
  }
  return out;
};

const fillPlaceholders = (str, ctx) =>
  String(str || '').replace(/\{(\w+)\}/g, (_, k) => (ctx[k] != null ? ctx[k] : `{${k}}`));

/**
 * Detects "ghost" placeholders : a `{word}` curly-brace pattern still present
 * after `fillPlaceholders`. Returns true if the string contains at least one
 * unreplaced variable — the caller should skip pushing the message in that
 * case (immersion-breaking bug otherwise).
 */
const _hasUnreplacedPlaceholder = (str) => /\{[a-zA-Z]\w*\}/.test(String(str || ''));

/**
 * Instantiate a template into a concrete message ready to push into the inbox.
 * `ctx` provides placeholders + optional `senderId`, `senderName`, `driverId`.
 *
 * Returns `null` if any placeholder couldn't be filled (subject, body or
 * choice label still contains a raw `{var}` token). This guards against
 * "ghost messages" the user reported (raw `{driverName}` shown to the
 * player). The caller (`pushMessageFromTemplate`) skips the push when null.
 */
const _instantiateTemplate = (template, totalDays, ctx = {}) => {
  const filled = _normaliseContext(ctx);
  const subject = fillPlaceholders(template.subjectTemplate, filled);
  const body = fillPlaceholders(template.bodyTemplate, filled);
  const choices = (template.choices || []).map(c => ({
    id: c.id, label: fillPlaceholders(c.label, filled), effects: c.effects || [],
  }));
  if (_hasUnreplacedPlaceholder(subject) || _hasUnreplacedPlaceholder(body)) return null;
  if (choices.some(c => _hasUnreplacedPlaceholder(c.label))) return null;
  return {
    id: generateId(),
    templateId: template.id,
    senderType: template.senderType,
    senderName: filled.senderName || _defaultSenderName(template.senderType, filled),
    senderId: filled.senderId || null,
    tone: template.tone,
    kind: template.kind,
    subject,
    body,
    receivedDay: totalDays,
    expiresDay: totalDays + (template.ttlDays || 7),
    read: false,
    replied: false,
    resolvedChoiceId: null,
    choices,
    defaultChoiceId: template.defaultChoiceId || null,
    driverId: filled.driverId || null,
    payload: filled.payload || {},
  };
};

const _defaultSenderName = (type, ctx) => {
  switch (type) {
    case 'driver':     return ctx.driverName || 'Un chauffeur';
    case 'client':     return ctx.clientName || 'Un client';
    case 'supplier':   return 'Concession partenaire';
    case 'bank':       return 'Votre conseiller bancaire';
    case 'competitor': return ctx.competitorName || 'Un concurrent';
    case 'urssaf':     return 'URSSAF';
    case 'media':      return 'Logistique Magazine';
    case 'inspector':  return 'Inspection du Travail';
    case 'hr':         return 'Service RH';
    case 'news':       return 'Actu Commerce';
    case 'system':     return 'SCD';
    default:           return 'Anonyme';
  }
};

export const createMessageSlice = (set, get) => ({
  inbox: [],
  lastRandomMessageDay: 0,
  // 31/05/2026 — Deferred messages queue. Used to push a message N days
  // AFTER an event (e.g. Nathalie's follow-up the day after a depot
  // opens, an M&A closes, a senior is hired). Each entry has the shape
  // { dueDay: number, payload: <pushCustomMessage payload> }.
  // Drained by `flushDeferredMessages()` in `coreSlice.nextDay`.
  deferredMessages: [],

  /**
   * Enqueue a message to be delivered on (or after) `dueDay`. Idempotent
   * by `key` if provided — the same trigger fired twice won't double-push.
   */
  enqueueDeferredMessage: (dueDay, payload, key = null) => {
    const state = get();
    const queue = state.deferredMessages || [];
    if (key && queue.some(e => e.key === key)) return false;
    set({ deferredMessages: [...queue, { dueDay, payload, key }] });
    return true;
  },

  /**
   * Drain all deferred entries whose dueDay has arrived. Called once per
   * tick by coreSlice.nextDay. Pushes each payload through pushCustomMessage.
   */
  flushDeferredMessages: () => {
    const state = get();
    const queue = state.deferredMessages || [];
    if (queue.length === 0) return 0;
    const today = state.totalDays || 0;
    const due = queue.filter(e => e.dueDay <= today);
    const remaining = queue.filter(e => e.dueDay > today);
    if (due.length === 0) return 0;
    set({ deferredMessages: remaining });
    due.forEach(e => {
      try { get().pushCustomMessage(e.payload); }
      catch (err) { console.warn('[messageSlice] flush failed:', err); }
    });
    return due.length;
  },

  /**
   * Push a message instantiated from a template ID. Returns the new message
   * or null if the template doesn't exist OR if any placeholder couldn't be
   * resolved (ghost-message guard).
   *
   * `companyName` and `playerName` are auto-filled from the current state if
   * the caller didn't supply them, so internal call sites can stay concise.
   */
  pushMessageFromTemplate: (templateId, ctx = {}, extraFields = {}) => {
    const t = MESSAGE_TEMPLATES.find(x => x.id === templateId);
    if (!t) return null;
    const state = get();
    const enrichedCtx = {
      companyName: state.company?.name || 'SCD',
      playerName: state.company?.name || 'Patron',
      city: state.company?.city || 'votre région',
      ...ctx,
    };
    const msg = _instantiateTemplate(t, state.totalDays || 0, enrichedCtx);
    if (!msg) return null; // ghost-placeholder detected → silently skip.
    // 08/02/2026 — Extra fields like `isNarrative: true` (random roll
    // ambiance) ou `isBusinessAlert: true` (popup dédié type AO loupé)
    // permettent à la Messagerie de filtrer/différencier visuellement.
    const finalMsg = { ...msg, ...extraFields };
    const inbox = [finalMsg, ...(state.inbox || [])].slice(0, MAX_INBOX);
    // 06/02/2026 — P2 retour user : applique les autoEffects du template
    // (choc carburant, grève péages) en même temps qu'on pousse le message.
    // Effet posé sur l'état partagé avec un `expiresOnDay` pour qu'il
    // disparaisse tout seul.
    const statePatch = { inbox };
    const today = state.totalDays || 0;
    (t.autoEffects || []).forEach(eff => {
      if (eff.kind === 'fuelPriceShock' && Number.isFinite(eff.multiplier) && Number.isFinite(eff.durationDays)) {
        statePatch.fuelPriceModifier = {
          multiplier: eff.multiplier,
          expiresOnDay: today + eff.durationDays,
          source: eff.source || 'fuel-shock',
        };
      } else if (eff.kind === 'peageStrike' && Number.isFinite(eff.dailyCostPerVehicle) && Number.isFinite(eff.durationDays)) {
        statePatch.peageStrike = {
          dailyCostPerVehicle: eff.dailyCostPerVehicle,
          expiresOnDay: today + eff.durationDays,
          source: eff.source || 'strike',
        };
      } else if (eff.kind === 'retailNews' && eff.effet && state.retail) {
        // SCD — actu commerce : effet temporaire retail (trafic, conversion,
        // surcoût % CA, satisfaction…) via le même mécanisme que les événements.
        try {
          statePatch.retail = applyRetailEventEffect(
            statePatch.retail || state.retail, eff.effet, today, finalMsg.subject
          );
        } catch (err) { console.warn('[retail] news effect failed:', err); }
      }
    });
    set(statePatch);
    return finalMsg;
  },

  /**
   * Push any custom (off-template) message (e.g. event-bridged decisions).
   */
  pushCustomMessage: (msg) => {
    const state = get();
    const finalMsg = {
      id: generateId(),
      receivedDay: state.totalDays || 0,
      expiresDay: (state.totalDays || 0) + (msg.ttlDays || 7),
      read: false,
      replied: false,
      resolvedChoiceId: null,
      choices: [],
      defaultChoiceId: null,
      driverId: null,
      payload: {},
      ...msg,
    };
    // 11/02/2026 — Retour user prod « bug message mixés ». Garde-fou défensif :
    // si le body montre les signes d'une contamination (fragments de tender
    // mélangés à un template chauffeur, ou placeholder résiduel), on log
    // pour qu'on puisse diagnostiquer en remontée terrain. On ne bloque pas
    // le push (sinon on perd l'info), mais on flag le message.
    if (isCorruptedMessageBody(finalMsg.body)) {
      console.warn('[messageSlice] CORRUPTED body detected at push-time:', {
        id: finalMsg.id,
        templateId: finalMsg.templateId,
        senderType: finalMsg.senderType,
        senderName: finalMsg.senderName,
        bodyPreview: String(finalMsg.body || '').slice(0, 200),
      });
      finalMsg.__corrupted = true;
    }
    set({ inbox: [finalMsg, ...(state.inbox || [])].slice(0, MAX_INBOX) });
    return finalMsg;
  },

  markMessageRead: (id) => {
    const state = get();
    set({
      inbox: (state.inbox || []).map(m => m.id === id ? { ...m, read: true } : m),
    });
  },

  /**
   * Mark a message as replied without going through `applyMessageChoice` —
   * used by external CTAs (e.g. accept an office upgrade proposal) where
   * the side-effects are handled by a different slice. The `outcome`
   * parameter is stored as `resolvedChoiceId` for the audit log.
   */
  markMessageReplied: (id, outcome = 'accepted') => {
    const state = get();
    set({
      inbox: (state.inbox || []).map(m => m.id === id ? {
        ...m, replied: true, read: true, resolvedChoiceId: outcome,
      } : m),
    });
  },

  /**
   * Apply a choice on a message — mutates treasury, drivers, reputation, etc.
   * Updates the message to `replied`. Returns { ok: true, applied: effects }.
   */
  applyMessageChoice: (messageId, choiceId) => {
    const state = get();
    const msg = (state.inbox || []).find(m => m.id === messageId);
    if (!msg) return { ok: false, reason: 'not_found' };
    if (msg.kind !== 'decision') return { ok: false, reason: 'not_decision' };
    if (msg.replied) return { ok: false, reason: 'already_replied' };
    const choice = (msg.choices || []).find(c => c.id === choiceId);
    if (!choice) return { ok: false, reason: 'bad_choice' };

    // 17/06/2026 — Opportunité commerciale : vérifier la disponibilité de la
    // flotte AVANT toute mutation. Refuser proprement si saturé.
    const commercialOppEff = (choice.effects || []).find(e => e.kind === 'commercialOpportunity');
    if (commercialOppEff) {
      const hasIdleVehicle = (state.vehicles || []).some(v =>
        v.status === 'idle' && !v.assignedContractId
      );
      const hasIdleDriver = (state.drivers || []).some(d =>
        d.status === 'idle' && !d.onHoliday && !d.assignedVehicleId
      );
      if (!hasIdleVehicle || !hasIdleDriver) {
        return {
          ok: false,
          reason: 'fleet_saturated',
          missing: !hasIdleVehicle ? 'vehicle' : 'driver',
        };
      }
    }

    // Compute the diff to apply.
    const patch = {};
    // Track burnout-alert resolutions for the annual report. Counter is
    // reset on Jan 1st by coreSlice.
    if (msg.templateId === 'drv-burnout-alert-01') {
      patch.burnoutAlertsResolvedYearly = (state.burnoutAlertsResolvedYearly || 0) + 1;
    }
    // 25/07/2026 (retour user Bilan RH « 0 démissions évitées, c'est
    // faux ») — toute rétention ACTIVE est tracée pour le rapport annuel :
    // renégociation accordée, contre-débauchage, alignement de salaire,
    // congés accordés sous pression. Remis à zéro au 1er janvier.
    const _addRetentionSave = (type, name) => {
      patch.retentionSavesYearly = [
        ...(patch.retentionSavesYearly ?? state.retentionSavesYearly ?? []),
        { day: state.totalDays || 0, type, name: name || null },
      ];
    };
    if (msg.driverId && ((msg.templateId === 'drv-renegotiation-low-sat-01' && choice.id === 'accord_full')
      || (msg.templateId === 'comp-poaching-01' && choice.id === 'counter'))) {
      const _saved = (state.drivers || []).find(d => d.id === msg.driverId);
      _addRetentionSave(msg.templateId === 'comp-poaching-01' ? 'poach' : 'renego', _saved && _saved.name);
    }
    // 10/02/2026 (Lot N P1) — Retour user prod : « demande d'augmentation
    // de salaires à intervalles trop proche (Amina Touré 2 demandes en
    // 2 jours) ». Stamping de la décision pour cooldown différencié :
    //   • 180 j si accepté (full ou partial)
    //   • 60 j si refusé
    //
    // 11/02/2026 (Lot N suite — fix Amina récurrent) — Le stamping était
    // limité à `drv-raise-01` (chemin aléatoire). Or Amina spammait via le
    // template `drv-raise-after-levelup-01` (déclenché à chaque level-up),
    // pour lequel `lastRaiseDecisionDay` n'était JAMAIS mis à jour. Résultat :
    // le gate dans hrSlice retombait sur le fallback `lastRaiseRequestDay`
    // 90 j au lieu des 180/60 j attendus. On unifie maintenant le stamping
    // pour les deux templates de demande de revalorisation. Le 3ᵉ template
    // (`drv-renegotiation-low-sat-01`) a son propre cooldown via
    // `renegotiationCooldown` (90 j) géré dans driverAlertsTick → pas besoin
    // de le stamper ici sous peine de double cadenas.
    const _isRaiseDecision = (msg.templateId === 'drv-raise-01'
      || msg.templateId === 'drv-raise-after-levelup-01')
      && msg.driverId;
    if (_isRaiseDecision) {
      const _raiseGranted = (choice.effects || []).some(
        e => (e.kind === 'driverRaise' && (e.amount || 0) > 0)
          // Une contre-proposition (driverCounterOffer) est une démarche
          // active du joueur : on la considère comme « accepted » côté
          // cooldown (le chauffeur a obtenu une réponse engagée), même si
          // l'issue finale dépend du dé. Sinon, refuser → 60 j seulement.
          || e.kind === 'driverCounterOffer'
      );
      const _day = state.totalDays || 0;
      const _currentDrivers = patch.drivers ?? state.drivers;
      patch.drivers = _currentDrivers.map(d => d.id === msg.driverId ? {
        ...d,
        lastRaiseDecisionDay: _day,
        lastRaiseDecisionOutcome: _raiseGranted ? 'accepted' : 'refused',
      } : d);
    }
    // 25/07/2026 (choix user 3a) — TOUTE hausse accordée hors demande
    // formelle (contre-débauchage `comp-poaching-01`, renégociation
    // `accord_full`, etc.) déclenche aussi la tranquillité salariale :
    // stamp « accepted » → 12 mois sans nouvelle demande d'augmentation.
    if (!_isRaiseDecision && msg.driverId) {
      const _grantsRaise = (choice.effects || []).some(
        e => e.kind === 'driverRaise' && (e.amount || 0) > 0
      );
      if (_grantsRaise) {
        const _cur = patch.drivers ?? state.drivers;
        patch.drivers = _cur.map(d => d.id === msg.driverId ? {
          ...d,
          lastRaiseDecisionDay: state.totalDays || 0,
          lastRaiseDecisionOutcome: 'accepted',
        } : d);
      }
    }
    (choice.effects || []).forEach(eff => {
      switch (eff.kind) {
        case 'treasury':
          patch.treasury = (patch.treasury ?? state.treasury) + (eff.amount || 0);
          break;
        case 'reputation':
          patch.reputation = Math.max(0, Math.min(100, (patch.reputation ?? state.reputation) + (eff.amount || 0)));
          break;
        case 'notoriety':
          patch.notoriety = Math.max(0, Math.min(100, (patch.notoriety ?? (state.notoriety || 0)) + (eff.amount || 0)));
          break;
        case 'creditScore':
          patch.creditScore = (patch.creditScore ?? state.creditScore ?? 50) + (eff.amount || 0);
          break;
        // 15/06/2026 — fix : l'offre de placement bancaire (bank-product-01)
        // débitait la trésorerie SANS créer le placement. Ce kind crée une
        // vraie entrée `activePlacements` (visible Banque → Placements, avec
        // intérêts mensuels). Si la trésorerie est insuffisante, rien n'est
        // débité ni placé.
        case 'placement': {
          const amount = eff.amount || 0;
          const curTreasury = patch.treasury ?? state.treasury;
          if (amount <= 0 || curTreasury < amount) break;
          patch.treasury = curTreasury - amount;
          const rate = (eff.ratePct ?? 2.5) / 100;
          const cur = patch.activePlacements ?? state.activePlacements ?? [];
          // Même structure que bankSlice.subscribePlacement (Banque →
          // Placements : intérêts mensuels + retrait à terme).
          patch.activePlacements = [...cur, {
            id: generateId(),
            bankId: eff.bankId || 'bnb_mariba',
            name: eff.name || 'Compte à terme entreprise',
            amount,
            rate,
            startDay: state.totalDays || 0,
            term: eff.lockMonths ?? 12,
            liquid: false,
            monthlyInterest: Math.round(amount * rate / 12),
            taxBonus: 0,
          }];
          break;
        }
        case 'driverRaise': {
          if (!msg.driverId) break;
          const current = patch.drivers ?? state.drivers;
          patch.drivers = current.map(d => d.id === msg.driverId ? {
            ...d,
            // Raise both the gross salary AND the employer cost (payroll
            // charges add ~42 % on top, consistent with hireDriver scaling).
            salaryGross: (d.salaryGross || 0) + (eff.amount || 0),
            employerCost: (d.employerCost || 0) + Math.round((eff.amount || 0) * 1.42),
          } : d);
          break;
        }
        case 'driverSatisfaction': {
          if (!msg.driverId) break;
          const current = patch.drivers ?? state.drivers;
          patch.drivers = current.map(d => d.id === msg.driverId ? {
            ...d,
            satisfaction: Math.max(0, Math.min(100, (d.satisfaction || 0) + (eff.amount || 0))),
          } : d);
          break;
        }
        case 'driverVacation': {
          if (!msg.driverId) break;
          const days = Math.max(1, eff.days || 5);
          const current = patch.drivers ?? state.drivers;
          const targetDriver = current.find(d => d.id === msg.driverId);
          // 08/02/2026 — Q2 bug Sébastien Leclerc : on garde une trace du
          // véhicule + contrat AVANT de mettre tout en pause, afin que le
          // chauffeur reprenne automatiquement sa mission au retour de
          // congés (driverFatigueTick auto-return branch).
          const preVacationVehicleId = targetDriver?.assignedVehicleId || null;
          let preVacationContractId = null;
          if (preVacationVehicleId) {
            const ctr = (state.activeContracts || []).find(
              c => c.driverId === msg.driverId || c.vehicleId === preVacationVehicleId
            );
            if (ctr) preVacationContractId = ctr.id;
          }
          // 08/02/2026 — Future/Backlog : 4ᵉ choix « sous-traiter ».
          // Si eff.subcontract === true, le véhicule reste ACTIF (un
          // intérimaire prend le volant), le contrat continue de générer
          // du CA. Le coût de la sous-traitance (1 500 €) est appliqué
          // par l'effet treasury séparé du choix.
          const useSubcontract = !!(eff.subcontract && preVacationVehicleId);
          if (targetDriver?.assignedVehicleId && !useSubcontract) {
            const vehicles = patch.vehicles ?? state.vehicles;
            patch.vehicles = vehicles.map(v => v.id === targetDriver.assignedVehicleId ? {
              ...v,
              assignedDriverId: null,
              // If the vehicle was active, the contract is now stalled — set
              // it to idle so the daily tick doesn't pay revenue.
              status: v.status === 'active' ? 'idle' : v.status,
            } : v);
          }
          // Subcontract path: tag the vehicle so the daily revenue tick
          // still pays out (interim driver), but mark with `subcontracted`
          // and `subcontractUntil` for UI + cost tracking.
          //
          // 08/02/2026 — Q2 prod bug : le coût de la sous-traitance était
          // hardcodé à -1500 € flat, indépendant de la durée d'absence
          // ET du tier du véhicule. Désormais : coût = `days ×
          // INTERIM_DAILY_TARIFF[tier]` (mêmes tarifs que l'intérim auto
          // sur congés annuels). Le -1500 statique a été retiré du template,
          // c'est ce bloc qui débite la trésorerie au juste prix.
          if (targetDriver?.assignedVehicleId && useSubcontract) {
            const vehicles = patch.vehicles ?? state.vehicles;
            const assignedVehicle = vehicles.find(v => v.id === targetDriver.assignedVehicleId);
            const tier = assignedVehicle?.tier || 1;
            const dailyCost = getInterimDailyCost(tier);
            const subcontractCost = dailyCost * days;
            patch.treasury = (patch.treasury ?? state.treasury) - subcontractCost;
            // 17/07/2026 (user prod « la sous-traitance ne s'est pas activée,
            // le toggle devrait suivre le choix ») — on arme aussi le toggle
            // intérim du véhicule (useInterim) pour que la Flotte reflète
            // l'état. `interimAutoActivated` permet de le désarmer
            // automatiquement au retour du titulaire. Pas de double
            // facturation : le tick quotidien d'intérim (coreSlice) ignore
            // les véhicules déjà couverts par la fenêtre `subcontracted`
            // (coût payé d'avance ici).
            patch.vehicles = vehicles.map(v => v.id === targetDriver.assignedVehicleId ? {
              ...v,
              subcontracted: true,
              subcontractUntil: state.totalDays + days,
              subcontractDailyCost: dailyCost,
              subcontractTotalCost: subcontractCost,
              useInterim: true,
              interimAutoActivated: true,
            } : v);
          }
          // 17/07/2026 (user prod — soldes de congés incohérents) : les
          // congés accordés via la messagerie ne débitaient PAS le solde
          // (Nadia : 2 sessions mais −5 j seulement ; Antoni : 1 session,
          // solde intact). On déduit désormais les jours et on trace le
          // compteur annuel « pris ».
          patch.drivers = current.map(d => d.id === msg.driverId ? {
            ...d,
            status: 'vacation',
            vacationUntil: state.totalDays + days,
            holidayDaysRemaining: Math.max(0, Math.round(((d.holidayDaysRemaining || 0) - days) * 10) / 10),
            holidayDaysTakenYear: Math.round(((d.holidayDaysTakenYear || 0) + days) * 10) / 10,
            assignedVehicleId: null,
            // Stash assignment for resume on vacation return.
            preVacationVehicleId,
            preVacationContractId,
            preVacationSubcontract: useSubcontract,
          } : d);
          break;
        }
        case 'driverSick': {
          if (!msg.driverId) break;
          const days = Math.max(1, eff.days || 5);
          const current = patch.drivers ?? state.drivers;
          const targetDriver = current.find(d => d.id === msg.driverId);
          if (targetDriver?.assignedVehicleId) {
            const vehicles = patch.vehicles ?? state.vehicles;
            patch.vehicles = vehicles.map(v => v.id === targetDriver.assignedVehicleId ? {
              ...v,
              assignedDriverId: null,
              status: v.status === 'active' ? 'idle' : v.status,
            } : v);
          }
          patch.drivers = current.map(d => d.id === msg.driverId ? {
            ...d,
            status: 'sick',
            sickUntil: state.totalDays + days,
            assignedVehicleId: null,
          } : d);
          break;
        }
        // 04/02/2026 — Phase Q1 : choix CET fin d'année.
        // `holidayPayoff` paie les jours restants en bulletin de décembre :
        //   trésorerie -= days × dailyCost ; satisfaction +5
        // `cetTransfer` reporte les jours sur le Compte Épargne Temps :
        //   driver.holidayDaysCet += days ; gratuit
        case 'holidayPayoff': {
          if (!msg.driverId) break;
          const current = patch.drivers ?? state.drivers;
          const target = current.find(d => d.id === msg.driverId);
          if (!target) break;
          const days = target.holidayDaysRemaining || 0;
          if (days <= 0) break;
          const dailyCost = (target.employerCost || target.salaryGross || 2800) / 21.7;
          const cost = Math.round(dailyCost * days);
          patch.treasury = (patch.treasury ?? state.treasury) - cost;
          patch.drivers = current.map(d => d.id === msg.driverId ? {
            ...d,
            holidayDaysRemaining: 0,
            satisfaction: Math.min(100, (d.satisfaction || 0) + 5),
          } : d);
          break;
        }
        case 'cetTransfer': {
          if (!msg.driverId) break;
          const current = patch.drivers ?? state.drivers;
          const target = current.find(d => d.id === msg.driverId);
          if (!target) break;
          const days = target.holidayDaysRemaining || 0;
          if (days <= 0) break;
          patch.drivers = current.map(d => d.id === msg.driverId ? {
            ...d,
            holidayDaysRemaining: 0,
            holidayDaysCet: (d.holidayDaysCet || 0) + days,
          } : d);
          break;
        }
        case 'driverFire': {
          if (!msg.driverId) break;
          const current = patch.drivers ?? state.drivers;
          // 08/02/2026 — Q2 user fix : archive trainings BEFORE removal so
          // a future rehire restores them (cf. firedDriversArchive used by
          // attrition + fireDriver).
          const dropped = current.find(d => d.id === msg.driverId);
          if (dropped) {
            const newArchive = { ...(patch.firedDriversArchive ?? state.firedDriversArchive ?? {}) };
            newArchive[dropped.dataId] = {
              level: dropped.level,
              xp: dropped.xp,
              specialty: dropped.specialty,
              specialty2: dropped.specialty2,
              extraSpecialties: dropped.extraSpecialties || [],
              licenses: dropped.licenses,
              competence: dropped.competence,
              accidentRisk: dropped.accidentRisk,
              employerCost: dropped.employerCost,
              trainings: dropped.trainings || [],
              totalTrips: dropped.totalTrips || 0,
              totalKm: dropped.totalKm || 0,
              firedAt: state.totalDays,
            };
            patch.firedDriversArchive = newArchive;
          }
          patch.drivers = current.filter(d => d.id !== msg.driverId);
          const vehicles = patch.vehicles ?? state.vehicles;
          patch.vehicles = vehicles.map(v => v.assignedDriverId === msg.driverId ? { ...v, assignedDriverId: null } : v);
          // 23/07/2026 — fix user prod « chauffeurs qui disparaissent » :
          // TOUT driverFire (y compris via choix par défaut d'un message
          // expiré) pousse désormais une notification visible.
          if (dropped) {
            patch.notifications = [...(patch.notifications ?? state.notifications ?? []).slice(-30), {
              id: `driver-fire-msg-${dropped.id}-${state.totalDays}`,
              type: 'driver_left',
              title: `${dropped.name} quitte l'entreprise`,
              message: `${dropped.name} a quitté vos effectifs (« ${msg.subject || 'décision messagerie'} »). Formations archivées — réembauche possible plus tard.`,
              day: state.totalDays,
              read: false,
            }];
          }
          break;
        }
        // 23/07/2026 (fix user prod « chauffeurs qui disparaissent ») —
        // remplace le driverFire immédiat du refus de renégociation par le
        // préavis légal VISIBLE de 30 j (bannière RH + lettre align/accept),
        // identique au dernier round refusé de driverCounterOffer.
        case 'driverStartDeparture': {
          if (!msg.driverId) break;
          const current = patch.drivers ?? state.drivers;
          const target = current.find(d => d.id === msg.driverId);
          if (!target || target.pendingDeparture) break;
          const noticeDays = 30;
          const expected = (msg.payload && msg.payload.expectedRaise) || eff.amount || 300;
          patch.drivers = current.map(d => d.id === msg.driverId ? {
            ...d,
            satisfaction: Math.max(0, Math.min(100, (d.satisfaction || 0) - 12)),
            pendingDeparture: {
              noticeUntil: state.totalDays + noticeDays,
              expectedRaise: expected,
              triggeredOn: state.totalDays,
              reason: 'renegotiation_refused',
            },
          } : d);
          patch.notifications = [...(patch.notifications ?? state.notifications ?? []).slice(-30), {
            id: `renego-notice-${target.id}-${state.totalDays}`,
            type: 'driver_departure_notice',
            title: `Préavis de départ — ${target.name}`,
            message: `${target.name} dépose un préavis de ${noticeDays} jours suite au refus de renégociation. Consultez sa lettre en messagerie pour un dernier compromis.`,
            day: state.totalDays,
            read: false,
          }];
          patch._pendingDepartureLetters = [
            ...(patch._pendingDepartureLetters ?? state._pendingDepartureLetters ?? []),
            {
              driverId: target.id,
              driverName: target.name,
              expectedRaise: expected,
              noticeDays,
              day: state.totalDays,
            },
          ];
          break;
        }
        // 23/07/2026 (choix user 3a) — lettre « pression congés » : le
        // joueur accorde 5 j de congés immédiatement → préavis annulé,
        // compteur de pression remis à zéro, intérim auto si en mission.
        case 'driverGrantPressureHoliday': {
          if (!msg.driverId) break;
          // Flush des mutations en attente puis délégation à l'action
          // sendDriverOnHoliday (même pattern que trainDriver).
          if (patch.drivers) set({ drivers: patch.drivers });
          if (patch.vehicles) set({ vehicles: patch.vehicles });
          if (patch.treasury !== undefined) set({ treasury: patch.treasury });
          if (patch.notifications) set({ notifications: patch.notifications });
          const res = typeof get().sendDriverOnHoliday === 'function'
            ? get().sendDriverOnHoliday(msg.driverId)
            : { ok: false, reason: 'unavailable' };
          const _target = get().drivers.find(d => d.id === msg.driverId);
          // Déjà en congés (auto-trigger du même tick) → la demande est de
          // fait satisfaite : on annule quand même le préavis.
          const granted = (res && res.ok) || (_target && (_target.onHoliday || _target.status === 'holiday'));
          if (granted) {
            set({
              drivers: get().drivers.map(d => d.id === msg.driverId ? {
                ...d,
                pendingDeparture: null,
                holidayPressureLossThisYear: 0,
                satisfaction: Math.min(100, (d.satisfaction || 0) + 10),
              } : d),
            });
            _addRetentionSave('pressure', _target && _target.name);
          } else {
            patch._grantHolidayError = res?.reason || 'unknown';
          }
          patch.drivers = get().drivers;
          patch.vehicles = get().vehicles;
          patch.treasury = get().treasury;
          patch.notifications = get().notifications;
          break;
        }
        case 'driverCounterOffer': {
          // Renegotiation: player offers `amount` (€/mo raise) vs the
          // driver's `expectedRaise` carried by msg.payload. Roll an
          // acceptance probability based on the ratio and the driver's
          // personality. If accepted → raise + sat boost. If refused →
          // 09/02/2026 — Q2 Lot I user prod : 1-3 rounds selon traits.
          //   • mercenaire / ambitieux → 3 rounds, exige +12-20 % vs marché
          //   • loyal / casanier → 1 round, accepte ±5 %
          //   • prudent / perfectionniste → 2 rounds, exige +6-10 %
          //   • Standard → 2 rounds, exige +5-8 %
          // Après le dernier round refusé → préavis légal de 30 j.
          if (!msg.driverId) break;
          const current = patch.drivers ?? state.drivers;
          const target = current.find(d => d.id === msg.driverId);
          if (!target) break;
          const expected = msg.payload?.expectedRaise ?? eff.expected ?? 200;
          const offer = eff.amount || 0;
          const currentRound = msg.payload?.round ?? 1;
          // Détermine maxRounds selon traits
          const traits = target.personalityTraits || [];
          const isHotTemper = traits.some(t => t === 'mercenaire' || t === 'ambitieux');
          const isLoyal = traits.some(t => t === 'loyal' || t === 'casanier');
          const isPrudent = traits.some(t => t === 'prudent' || t === 'perfectionniste');
          const maxRounds = isHotTemper ? 3 : isLoyal ? 1 : isPrudent ? 2 : 2;
          // Tolérance d'acceptation : étroite pour hot temper, large pour loyal
          // Loyal accepte à 95 % de l'attendu (±5 %), hot temper exige 100 %+
          const acceptanceFloor = isLoyal ? 0.95 : isPrudent ? 0.92 : isHotTemper ? 1.00 : 0.95;
          const ratio = offer / Math.max(1, expected);
          // Pente d'acceptation : douce pour loyal, dure pour hot temper
          let proba = ratio >= acceptanceFloor ? 0.80 : ratio >= acceptanceFloor - 0.10 ? 0.40 : 0.10;
          if (traits.includes('pragmatique')) proba += 0.15;
          if (isHotTemper) proba -= 0.15;
          if (isLoyal) proba += 0.20;
          proba = Math.max(0.05, Math.min(0.95, proba));
          const accepts = Math.random() < proba;
          if (accepts) {
            patch.drivers = current.map(d => d.id === msg.driverId ? {
              ...d,
              salaryGross: (d.salaryGross || 0) + offer,
              employerCost: Math.round((d.employerCost || 0) + offer * 1.42),
              satisfaction: Math.max(0, Math.min(100, (d.satisfaction || 0) + 8)),
              lastRaiseDecisionDay: state.totalDays || 0,
              lastRaiseDecisionOutcome: 'accepted',
            } : d);
            _addRetentionSave('renego', target.name);
            patch.notifications = [...(patch.notifications ?? state.notifications ?? []).slice(-30), {
              id: `renego-ok-${target.id}-${state.totalDays}`,
              type: 'driver_renegotiation_accepted',
              title: 'Contre-proposition acceptée',
              message: `${target.name} a accepté votre contre-proposition de +${offer.toLocaleString('fr-FR')} €/mois après ${currentRound} round(s).`,
              day: state.totalDays,
              read: false,
            }];
          } else if (currentRound < maxRounds) {
            // Round suivant : le chauffeur revient avec une exigence ajustée
            // (légèrement plus dure car frustré).
            const newExpected = Math.round(expected * (isHotTemper ? 1.10 : 1.05));
            patch._pendingNegotiationRounds = [
              ...(state._pendingNegotiationRounds || []),
              {
                driverId: target.id,
                driverName: target.name,
                round: currentRound + 1,
                maxRounds,
                expectedRaise: newExpected,
                previousOffer: offer,
                day: state.totalDays,
              },
            ];
            patch.drivers = current.map(d => d.id === msg.driverId ? {
              ...d,
              satisfaction: Math.max(0, Math.min(100, (d.satisfaction || 0) - 3)),
            } : d);
            patch.notifications = [...(patch.notifications ?? state.notifications ?? []).slice(-30), {
              id: `renego-round-${target.id}-${currentRound + 1}-${state.totalDays}`,
              type: 'driver_renegotiation_continued',
              title: `Négociation — round ${currentRound + 1}/${maxRounds}`,
              message: `${target.name} refuse votre offre et revient avec une exigence ajustée. Consultez la messagerie.`,
              day: state.totalDays,
              read: false,
            }];
          } else {
            // Refus définitif (dernier round) ⇒ préavis légal 30 j
            const noticeDays = 30;
            patch.drivers = current.map(d => d.id === msg.driverId ? {
              ...d,
              satisfaction: Math.max(0, Math.min(100, (d.satisfaction || 0) - 12)),
              pendingDeparture: {
                noticeUntil: state.totalDays + noticeDays,
                expectedRaise: expected,
                triggeredOn: state.totalDays,
                reason: 'counter_refused',
                roundsUsed: currentRound,
              },
            } : d);
            patch.notifications = [...(patch.notifications ?? state.notifications ?? []).slice(-30), {
              id: `renego-notice-${target.id}-${state.totalDays}`,
              type: 'driver_departure_notice',
              title: `Préavis de départ — ${target.name}`,
              message: `${target.name} dépose un préavis de ${noticeDays} jours après ${currentRound} round(s) de négociation. Consultez sa lettre en messagerie pour un dernier compromis.`,
              day: state.totalDays,
              read: false,
            }];
            patch._pendingDepartureLetters = [
              ...(state._pendingDepartureLetters || []),
              {
                driverId: target.id,
                driverName: target.name,
                expectedRaise: expected,
                noticeDays,
                day: state.totalDays,
              },
            ];
          }
          break;
        }
        case 'driverAlignSalary': {
          // 08/02/2026 — Q2 user fix : dernière chance — alignement sur
          // le montant attendu par le chauffeur. Supprime le préavis.
          if (!msg.driverId) break;
          const current = patch.drivers ?? state.drivers;
          const target = current.find(d => d.id === msg.driverId);
          if (!target) break;
          const raise = (target.pendingDeparture && target.pendingDeparture.expectedRaise) || eff.amount || 0;
          patch.drivers = current.map(d => d.id === msg.driverId ? {
            ...d,
            salaryGross: (d.salaryGross || 0) + raise,
            employerCost: Math.round((d.employerCost || 0) + raise * 1.42),
            satisfaction: Math.min(100, (d.satisfaction || 0) + 15),
            pendingDeparture: null,
            // 25/07/2026 — hausse obtenue → 12 mois de tranquillité.
            lastRaiseDecisionDay: state.totalDays || 0,
            lastRaiseDecisionOutcome: 'accepted',
          } : d);
          _addRetentionSave('align', target.name);
          patch.notifications = [...(patch.notifications ?? state.notifications ?? []).slice(-30), {
            id: `renego-aligned-${target.id}-${state.totalDays}`,
            type: 'driver_renegotiation_aligned',
            title: 'Préavis annulé',
            message: `${target.name} a accepté de rester suite à l'alignement de salaire (+${raise.toLocaleString('fr-FR')} €/mois).`,
            day: state.totalDays,
            read: false,
          }];
          break;
        }
        case 'driverAcceptDeparture': {
          // 08/02/2026 — Q2 user fix : confirmation explicite du départ.
          // Le chauffeur quitte immédiatement (préavis purgé) MAIS ses
          // formations sont archivées pour un éventuel ré-engagement.
          if (!msg.driverId) break;
          const current = patch.drivers ?? state.drivers;
          const dropped = current.find(d => d.id === msg.driverId);
          if (dropped) {
            // 23/07/2026 (choix user 3a) — départ « pression congés » :
            // l'indemnité conventionnelle est débitée à l'acte.
            const _indemnity = dropped.pendingDeparture?.reason === 'holiday_pressure'
              ? (dropped.pendingDeparture.indemnity || 0) : 0;
            if (_indemnity > 0) {
              patch.treasury = (patch.treasury ?? state.treasury) - _indemnity;
            }
            patch.notifications = [...(patch.notifications ?? state.notifications ?? []).slice(-30), {
              id: `depart-acted-${dropped.id}-${state.totalDays}`,
              type: 'driver_departure_final',
              title: `Départ acté — ${dropped.name}`,
              message: _indemnity > 0
                ? `${dropped.name} quitte l'entreprise (rupture négociée, indemnité −${_indemnity.toLocaleString('fr-FR')} €). Formations archivées.`
                : `${dropped.name} quitte l'entreprise. Formations archivées pour un éventuel retour.`,
              day: state.totalDays,
              read: false,
            }];
            const newArchive = { ...(patch.firedDriversArchive ?? state.firedDriversArchive ?? {}) };
            newArchive[dropped.dataId] = {
              level: dropped.level,
              xp: dropped.xp,
              specialty: dropped.specialty,
              specialty2: dropped.specialty2,
              extraSpecialties: dropped.extraSpecialties || [],
              licenses: dropped.licenses,
              competence: dropped.competence,
              accidentRisk: dropped.accidentRisk,
              employerCost: dropped.employerCost,
              trainings: dropped.trainings || [],
              totalTrips: dropped.totalTrips || 0,
              totalKm: dropped.totalKm || 0,
              firedAt: state.totalDays,
            };
            patch.firedDriversArchive = newArchive;
          }
          patch.drivers = current.filter(d => d.id !== msg.driverId);
          const vehicles = patch.vehicles ?? state.vehicles;
          patch.vehicles = vehicles.map(v => v.assignedDriverId === msg.driverId ? { ...v, assignedDriverId: null } : v);
          break;
        }
        case 'driverUnassign': {
          // Soft action: free the driver from his vehicle (mission stops) but
          // keep him on payroll. Used by burnout-alert "Reassign" choice.
          if (!msg.driverId) break;
          const current = patch.drivers ?? state.drivers;
          const targetDriver = current.find(d => d.id === msg.driverId);
          if (targetDriver?.assignedVehicleId) {
            const vehicles = patch.vehicles ?? state.vehicles;
            patch.vehicles = vehicles.map(v => v.id === targetDriver.assignedVehicleId ? {
              ...v,
              assignedDriverId: null,
              status: v.status === 'active' ? 'idle' : v.status,
            } : v);
          }
          patch.drivers = current.map(d => d.id === msg.driverId ? {
            ...d,
            assignedVehicleId: null,
            status: 'idle',
          } : d);
          break;
        }
        case 'driverBonus': {
          // One-shot cash bonus that boosts satisfaction (existing giveBonus
          // action operates similarly but with a payroll context). Used by
          // burnout-alert "Prime exceptionnelle" choice.
          if (!msg.driverId) break;
          const cost = Math.abs(eff.amount || 500);
          patch.treasury = (patch.treasury ?? state.treasury) - cost;
          const current = patch.drivers ?? state.drivers;
          patch.drivers = current.map(d => d.id === msg.driverId ? {
            ...d,
            satisfaction: Math.max(0, Math.min(100, (d.satisfaction || 0) + 15)),
          } : d);
          break;
        }
        case 'creditOffer': {
          // Light-weight: just credit the cash without compounding interest
          // (full loan UX lives on Banque page). Approximate the monthly
          // payment so the player sees a coherent cost in their P&L.
          patch.treasury = (patch.treasury ?? state.treasury) + (eff.amount || 0);
          const monthlyPayment = Math.round((eff.amount || 0) * (1 + (eff.rate || 0)) / Math.max(1, (eff.months || 36)));
          const credits = [...(state.credits || []), {
            id: `credit-msg-${Date.now()}`,
            principal: eff.amount || 0,
            monthlyPayment,
            annualRate: eff.rate || 0.04,
            durationMonths: eff.months || 36,
            remainingMonths: eff.months || 36,
            // 19/07/2026 — user prod : ce champ manquait → après le 1er
            // amortissement mensuel il tombait à 0 pour toujours et le
            // bouton « Solder le crédit » restait grisé à vie.
            remainingPrincipal: eff.amount || 0,
            startDay: state.totalDays,
            source: 'message_offer',
          }];
          patch.credits = credits;
          break;
        }
        // 08/02/2026 — Q2 user prod : chauffeur a demandé une formation
        // (template `drv-self-training-request-01`). Le joueur accepte
        // → on délègue à `get().trainDriver(driverId, trainingType)`.
        // Si trainDriver échoue (chauffeur en mission, trésorerie KO),
        // on stocke la raison pour debug mais on n'interrompt pas la
        // résolution du message (le joueur a dit oui).
        case 'trainDriver': {
          const targetDriverId = msg.driverId;
          if (targetDriverId && typeof get().trainDriver === 'function') {
            // 22/07/2026 — bug user prod « boucle formations » : trainDriver
            // fait son propre set() ; les effets suivants (driverSatisfaction)
            // repartaient d'un state.drivers PÉRIMÉ et le set() final écrasait
            // la formation (statut, résultats, historique trainings[] → le
            // cooldown anti-spam de 360 j ne voyait jamais la formation et le
            // même chauffeur redemandait la même formation en boucle, débitée
            // à chaque fois sans résultat visible).
            // Fix : pousser les mutations en attente, appeler trainDriver,
            // puis rebaser le patch sur le state FRAIS.
            if (patch.drivers) set({ drivers: patch.drivers });
            if (patch.treasury !== undefined) set({ treasury: patch.treasury });
            const res = get().trainDriver(targetDriverId, eff.trainingType);
            patch.drivers = get().drivers;
            patch.treasury = get().treasury;
            if (res && !res.ok) {
              patch._trainDriverError = res.reason;
            }
          }
          break;
        }
        case 'commercialOpportunity': {
          // 17/06/2026 — Handled post-set() via startCommercialOpportunity.
          // Le flag est juste un marqueur ; la mobilisation effective + récom-
          // pense différée est gérée par le commercialOpportunitySlice.
          break;
        }
        case 'convoyExceptionnelAccept': {
          // 05/02/2026 (Q1 retour user prod) — Marqueur. La mobilisation
          // effective (débit intérim + pose tracteur + chauffeur) se fait
          // POST-set() via startConvoyExceptionnel pour avoir un get()
          // cohérent avec l'inbox mise à jour.
          break;
        }
        case 'competitorRelation': {
          // 17/06/2026 (Lot 6 Phase 3) — Modifie la relation avec un
          // concurrent depuis un choix de message narratif (OPA refusée,
          // fusion acceptée, plainte réglée, etc.). Le competitorId vient
          // soit de eff.competitorId, soit du message lui-même
          // (msg.payload?.competitorId).
          const cid = eff.competitorId || msg.payload?.competitorId;
          if (cid && typeof get().adjustCompetitorRelation === 'function') {
            // Applique APRÈS le set final (pour éviter une rétro-écriture).
            // On ne met pas dans patch — on délègue.
            setTimeout(() => get().adjustCompetitorRelation(cid, eff.amount || 0, msg.templateId || 'message_choice'), 0);
          }
          break;
        }
        case 'flag': {
          // Pose un flag booléen sur le state pour gating futur (ex:
          // fusion acceptée → flag 'fusionWithLogistar' utilisable par
          // d'autres systèmes). Stocké dans `narrativeFlags`.
          if (eff.flag) {
            const cur = patch.narrativeFlags ?? state.narrativeFlags ?? {};
            patch.narrativeFlags = { ...cur, [eff.flag]: eff.value ?? true };
          }
          break;
        }
        default:
          break;
      }
    });

    // Mark message as replied.
    const newInbox = (state.inbox || []).map(m => m.id === messageId ? {
      ...m, replied: true, read: true, resolvedChoiceId: choiceId,
    } : m);

    // 11/02/2026 (Lot N suite 2 — UX enhancement) — Après une augmentation
    // EFFECTIVEMENT accordée (salaryGross réellement augmenté), on prépare
    // un message de remerciement personnalisé selon les traits du chauffeur
    // + un bonus +5 satisfaction. Ce bonus signale visuellement au joueur
    // que le cooldown 180j est bien armé.
    //
    // Détection : on compare le salaryGross dans patch.drivers (post-effets)
    // au state.drivers (pré-effets) pour le driverId du message. Ça couvre
    // les 3 cas qui peuvent produire une hausse :
    //   • drv-raise-01 → accord (+150) ou partial (+75)
    //   • drv-raise-after-levelup-01 → accord_full (+200)
    //   • driverCounterOffer accepté (rng-driven, peut produire la hausse)
    let _thanksAfterRaise = null;
    if (_isRaiseDecision && patch.drivers) {
      const _prevDriver = (state.drivers || []).find(d => d.id === msg.driverId);
      const _nextDriver = patch.drivers.find(d => d.id === msg.driverId);
      if (_prevDriver && _nextDriver
          && (_nextDriver.salaryGross || 0) > (_prevDriver.salaryGross || 0)) {
        // Apply the +5 satisfaction boost on top of any effect already applied
        // (e.g. the +12 from accord_full). Capped at 100.
        patch.drivers = patch.drivers.map(d => d.id === msg.driverId ? {
          ...d,
          satisfaction: Math.max(0, Math.min(100, (d.satisfaction || 0) + 5)),
        } : d);
        // Stash the picker output so we can push the message after set().
        _thanksAfterRaise = {
          templateId: pickThanksAfterRaiseTemplate(_nextDriver),
          driverId: msg.driverId,
          driverName: _nextDriver.name,
        };
      }
    }

    set({ ...patch, inbox: newInbox });

    // Push the thanks-after-raise message right after the main set() so the
    // template reads the post-raise driver state. Info-only, ttl 14 days.
    if (_thanksAfterRaise && typeof get().pushMessageFromTemplate === 'function') {
      try {
        get().pushMessageFromTemplate(_thanksAfterRaise.templateId, {
          driverId: _thanksAfterRaise.driverId,
          name: _thanksAfterRaise.driverName,
        });
      } catch (err) {
        console.warn('[messageSlice] thanks-after-raise push failed:', err);
      }
    }

    // 17/06/2026 (Lot 6 Phase L2) — Si le message est une procédure
    // juridique, on enregistre sa résolution dans l'historique pour le
    // bilan annuel Juliette + le score d'évaluation apprenant.
    if (msg.senderType === 'legal' && msg.payload?.legalProcedure
        && typeof get().recordLegalProcedureResolution === 'function') {
      const treasuryEff = (choice.effects || []).find(e => e.kind === 'treasury');
      try {
        get().recordLegalProcedureResolution({
          templateId: msg.templateId || msg.payload?.templateId,
          choiceId,
          day: state.totalDays || 0,
          cost: treasuryEff?.amount || 0,
        });
      } catch (err) {
        console.warn('[messageSlice] recordLegalProcedureResolution failed:', err);
      }
    }

    // 17/06/2026 — Si l'effet est `commercialOpportunity`, on déclenche la
    // mobilisation flotte+chauffeur APRÈS le set principal (le slice
    // utilise get()/set() en interne). La trésorerie + réputation sont
    // versées en fin de mission via tickCommercialOpportunities.
    if (commercialOppEff && typeof get().startCommercialOpportunity === 'function') {
      try {
        get().startCommercialOpportunity({
          clientName: msg.senderName || 'Client',
          treasuryReward: commercialOppEff.treasuryReward ?? 2500,
          reputationReward: commercialOppEff.reputationReward ?? 2,
        });
      } catch (err) {
        console.warn('[messageSlice] startCommercialOpportunity failed:', err);
      }
    }

    // 05/02/2026 (Q1 retour user prod) — Effet `convoyExceptionnelAccept` →
    // déclenche la mise en route immédiate de la mission convoi
    // exceptionnel (débit intérim + pose tracteur + chauffeur en active).
    const convoyAcceptEff = (choice.effects || []).find(e => e.kind === 'convoyExceptionnelAccept');
    if (convoyAcceptEff && typeof get().startConvoyExceptionnel === 'function') {
      try {
        const r = get().startConvoyExceptionnel({
          archetypeId: convoyAcceptEff.archetypeId,
          reward: convoyAcceptEff.reward,
          interimCost: convoyAcceptEff.interimCost,
          durationDays: convoyAcceptEff.durationDays,
        });
        if (!r.ok) {
          // Push an error message back so the user knows why the mission
          // didn't start (saturation, permit missing, treasury low, etc.).
          if (typeof get().pushCustomMessage === 'function') {
            get().pushCustomMessage({
              senderType: 'advisor',
              senderName: 'Direction opérations',
              tone: 'aggressive',
              kind: 'info',
              subject: 'Convoi exceptionnel — démarrage impossible',
              body:
                `Impossible de démarrer la mission : **${r.reason}**.\n\n` +
                `Vérifie le permis convoi exceptionnel actif, la dispo du tracteur ` +
                `Heavyhaul XXL (idle), un chauffeur CE libre, et la trésorerie suffisante ` +
                `pour l'intérim (${(convoyAcceptEff.interimCost || 0).toLocaleString('fr-FR')} €).`,
              ttlDays: 5,
            });
          }
        }
      } catch (err) {
        console.warn('[messageSlice] startConvoyExceptionnel failed:', err);
      }
    }

    return { ok: true, applied: choice.effects || [] };
  },

  /**
   * Daily tick — auto-expires unanswered decision messages with the default
   * choice; purges expired info messages after expiresDay + 7.
   * Also rolls a random narrative message (max 1 per day, ~50 % chance).
   */
  tickMessages: () => {
    const state = get();
    const day = state.totalDays || 0;

    // Apply default-choice on every expired-today decision.
    const expiredDecisions = (state.inbox || []).filter(m =>
      m.kind === 'decision' && !m.replied && (day >= m.expiresDay)
    );
    expiredDecisions.forEach(m => {
      let appliedChoiceLabel = null;
      if (m.defaultChoiceId) {
        const defaultChoice = (m.choices || []).find(c => c.id === m.defaultChoiceId);
        appliedChoiceLabel = defaultChoice?.label || null;
        get().applyMessageChoice(m.id, m.defaultChoiceId);
      } else {
        // Just mark replied with no effect.
        set(s => ({
          inbox: s.inbox.map(x => x.id === m.id ? { ...x, replied: true, read: true } : x),
        }));
      }
      // Push a system note so the player sees what happened during the tick.
      const note = appliedChoiceLabel
        ? `Vous n'avez pas répondu à temps à « ${m.subject} ». Choix par défaut appliqué : « ${appliedChoiceLabel} ».`
        : `Vous n'avez pas répondu à temps à « ${m.subject} ». Aucun effet appliqué.`;
      get().pushCustomMessage({
        senderType: 'system',
        senderName: 'SCD',
        tone: 'urgent',
        subject: 'Décision expirée',
        body: note,
        kind: 'info',
        ttlDays: 5,
      });
    });

    // Purge info messages older than expiresDay + 7 (keep some buffer).
    const cleaned = (get().inbox || []).filter(m => day < (m.expiresDay + 7));
    if (cleaned.length !== (get().inbox || []).length) {
      set({ inbox: cleaned });
    }

    // Random roll for a narrative message — at most once per day, ~50% chance.
    if (state.lastRandomMessageDay !== day && Math.random() < 0.5) {
      // Build a contextual snapshot so pickRandomTemplate can filter out
      // templates incompatible with the current state (no drivers/vehicles/contracts).
      const driverCount = (state.drivers || []).length;
      const vehicleActiveCount = (state.vehicles || []).filter(v => v.status === 'active' || v.status === 'idle').length;
      const activeContractsCount = (state.activeContracts || []).length;
      // Compute the tiers currently held by at least one recurring client so
      // the picker can filter tier-bound templates.
      const loyaltyMap = state.clientLoyalty || {};
      const tierSet = new Set();
      for (const cid of Object.keys(loyaltyMap)) {
        const tier = getLoyaltyTier((loyaltyMap[cid] || {}).score || 0);
        if (tier) tierSet.add(tier.id);
      }
      const tpl = pickRandomTemplate({
        totalDays: day,
        phase: state.phase || 1,
        driverCount,
        vehicleActiveCount,
        activeContractsCount,
        treasury: state.treasury || 0,
        clientLoyaltyTiers: tierSet.size > 0 ? Array.from(tierSet) : null,
      });
      if (!tpl) {
        set({ lastRandomMessageDay: day });
        return;
      }

      // ── Cross-sender placeholder detection ──────────────────────────────
      // A template can reference `{driverName}` while being sent by a
      // competitor (e.g. `comp-poaching-01`). We scan subject + body for
      // every known placeholder and decide what to populate accordingly.
      const fullText = `${tpl.subjectTemplate || ''} ${tpl.bodyTemplate || ''}`;
      const needsDriver = /\{driver(Name|FirstName|Level)\}/.test(fullText);
      const needsClient = /\{clientName\}/.test(fullText);
      const needsCity = /\{city\}/.test(fullText);
      const needsVehicleModel = /\{vehicleModel\}/.test(fullText);

      // Build context. Always supply company/city/playerName fallback so any
      // template using `{companyName}`/`{playerName}`/`{city}` stays clean.
      const ctx = {
        companyName: state.company?.name || 'SCD',
        playerName: state.company?.name || 'Patron',
        city: state.company?.city || 'votre région',
      };

      // ── Driver context ──────────────────────────────────────────────────
      if (needsDriver) {
        let pool = state.drivers || [];
        const isRaise    = tpl.id === 'drv-raise-01';
        const isVacation = tpl.id === 'drv-vacation-01';
        const isResign   = tpl.id === 'drv-resignation-warn-01';
        // 12/02/2026 (fix user prod « Antoni et Amaury reviennent
        // régulièrement de formations alors que formés une seule fois »)
        // — Ce template n'a AUCUN cooldown ni prérequis. La sélection
        // aléatoire uniforme pouvait donc retomber sur le même chauffeur
        // tous les 90 j (minDays du template), l'envoyer en formation
        // 3 j → cycle qui donne l'impression d'un chauffeur "hors du
        // game" à intervalles réguliers.
        // Fix : exclure les chauffeurs qui ont DÉJÀ suivi éco-conduite
        // (payload `type: 'ecoConduite'` dans `d.trainings[]`) ET dont
        // la dernière formation date de moins de 360 jours (cadence
        // annuelle acceptable pour un rappel volontaire).
        const isSelfTraining = tpl.id === 'drv-self-training-request-01';
        // 18/06/2026 — fix « arrêt maladie fantôme » (messagerie) : le
        // message aléatoire d'arrêt doit avoir un EFFET réel. On ne cible
        // que des chauffeurs disponibles (pas de mission interrompue sans
        // choix du joueur) et on les met réellement en arrêt 2 jours.
        const isIllness = tpl.id === 'drv-illness-01';
        if (isRaise && typeof get().computeExpectedSalary === 'function') {
          pool = pool.filter(d => {
            // 10/02/2026 (Lot N P1) — Cooldown différencié selon la dernière
            // décision : 180 j si acceptée (peut être relancée seulement 6
            // mois plus tard), 60 j si refusée (réessai au bout de 2 mois).
            // Fallback `lastRaiseRequestDay` historique : 90 j pour les
            // chauffeurs ayant juste demandé sans réponse.
            //
            // 11/02/2026 (Lot N suite — fix Amina récurrent) — Plancher dur
            // 90 j sur lastRaiseRequestDay TOUJOURS appliqué (en plus du
            // cooldown décisionnel), pour éviter la double spam quand le
            // cooldown 180 j vient d'expirer mais que la dernière demande
            // est récente.
            const lastReq = d.lastRaiseRequestDay || 0;
            // 25/07/2026 (choix user 3a) — plancher 90 → 180 j, cooldown
            // accepté 180 → 360 j, refusé 60 → 90 j.
            if (day - lastReq < 180) return false;
            const lastDec = d.lastRaiseDecisionDay || 0;
            const decisionOutcome = d.lastRaiseDecisionOutcome;
            if (lastDec > 0) {
              const cooldown = decisionOutcome === 'accepted' ? 360 : 90;
              if (day - lastDec < cooldown) return false;
            }
            const baseline = get().computeExpectedSalary(d.dataId) || 0;
            if (baseline > 0 && (d.salary || 0) > baseline * 1.5) return false;
            return true;
          });
        } else if (isVacation) {
          pool = pool.filter(d => (day - (d.hiredAt || 0)) >= 90);
        } else if (isResign) {
          pool = pool.filter(d => (day - (d.hiredAt || 0)) >= 30);
        } else if (isSelfTraining) {
          pool = pool.filter(d => {
            // Chauffeur déjà en formation / mission / congé : skip.
            if (d.status === 'training' || d.status === 'vacation'
                || d.status === 'sick' || d.status === 'holiday'
                || d.status === 'striking' || d.status === 'active') return false;
            // Cooldown 360 j après la dernière formation éco-conduite (ou
            // n'importe quelle formation), pour éviter que le même
            // chauffeur reçoive répétitivement l'invitation.
            const trainings = Array.isArray(d.trainings) ? d.trainings : [];
            const lastTraining = trainings.reduce((max, tr) => Math.max(max, tr.startDay || 0), 0);
            if (lastTraining > 0 && (day - lastTraining) < 360) return false;
            return true;
          });
        } else if (isIllness) {
          pool = pool.filter(d => d.status === 'idle');
        }
        if (pool.length === 0) {
          // No eligible driver → would emit a ghost message. Skip silently.
          set({ lastRandomMessageDay: day });
          return;
        }
        const d = pool[Math.floor(Math.random() * pool.length)];
        ctx.driverId = d.id;
        ctx.driverName = d.name;
        ctx.driverFirstName = (d.name || '').split(' ')[0] || d.name;
        ctx.driverLevel = d.level;
        if (isRaise) {
          set({
            drivers: state.drivers.map(dd => dd.id === d.id ? { ...dd, lastRaiseRequestDay: day } : dd),
          });
        }
        if (isIllness) {
          // Effet RÉEL : arrêt court de 2 jours (le chauffeur est idle,
          // aucune mission n'est interrompue). Retour auto via fatigueTick.
          const sickDays = 2;
          set({
            drivers: state.drivers.map(dd => dd.id === d.id ? {
              ...dd, status: 'sick', sickUntil: day + sickDays,
            } : dd),
          });
        }
      }

      // ── Client context ──────────────────────────────────────────────────
      if (needsClient) {
        // Priority 1 : if the template has a tierFilter, pick a recurring
        // client from `clientLoyalty` whose current tier matches the filter.
        // Falls back to activeContracts for legacy templates.
        let clientName = null;
        if (tpl.tierFilter && tpl.tierFilter.length > 0) {
          const matches = [];
          for (const cid of Object.keys(loyaltyMap)) {
            const tier = getLoyaltyTier((loyaltyMap[cid] || {}).score || 0);
            if (tier && tpl.tierFilter.includes(tier.id)) {
              const client = CLIENT_BY_ID[cid];
              if (client) matches.push(client);
            }
          }
          if (matches.length > 0) {
            const c = matches[Math.floor(Math.random() * matches.length)];
            clientName = c.name;
          }
        }
        if (!clientName) {
          const contracts = state.activeContracts || [];
          if (contracts.length === 0) {
            set({ lastRandomMessageDay: day });
            return;
          }
          const c = contracts[Math.floor(Math.random() * contracts.length)];
          clientName = c.clientName || c.name || 'Client';
        }
        ctx.clientName = clientName;
        if (!ctx.senderName) ctx.senderName = clientName;
      }

      // ── Vehicle context ─────────────────────────────────────────────────
      if (needsVehicleModel) {
        const fleet = state.vehicles || [];
        if (fleet.length > 0) {
          const v = fleet[Math.floor(Math.random() * fleet.length)];
          ctx.vehicleModel = v.model || v.name || 'poids lourd';
        } else {
          // Generic fallback — template is a supplier promo that can still
          // make sense without an owned vehicle (e.g. `sup-new-model-01`).
          ctx.vehicleModel = 'poids lourd dernière génération';
        }
      }

      // ── Sender-type extras (names, brand, etc.) ─────────────────────────
      if (tpl.senderType === 'competitor') {
        // 06/02/2026 — P2 retour user : on tire un VRAI concurrent (Ivan,
        // Geoffrey, Stéphane, Ruddy) au lieu d'un faux « voisin » générique.
        // Biais selon le ton du message + la relation actuelle :
        //  - aggressive → concurrent avec relation faible (≤ 40) en priorité,
        //  - friendly/formal → relation élevée (≥ 56) en priorité,
        //  - sinon (humour / neutre) → aléatoire.
        const relations = state.competitorRelations || {};
        const tone = tpl.tone || 'neutral';
        let pool = COMPETITORS;
        if (tone === 'aggressive') {
          const enemies = COMPETITORS.filter(c => (relations[c.id] ?? 50) <= 40);
          if (enemies.length > 0) pool = enemies;
        } else if (tone === 'friendly' || tone === 'formal') {
          const allies = COMPETITORS.filter(c => (relations[c.id] ?? 50) >= 56);
          if (allies.length > 0) pool = allies;
        }
        const comp = pool[Math.floor(Math.random() * pool.length)] || COMPETITORS[0];
        const directorName = comp.director?.name || comp.name;
        ctx.competitorName = comp.name;
        if (!ctx.senderName) ctx.senderName = directorName;
      } else if (tpl.senderType === 'supplier') {
        const SUP_NAMES = ['Grossidis Distribution', 'Comptoir & Maison', 'Hexa Négoce', 'Ateliers Réunis'];
        const name = SUP_NAMES[Math.floor(Math.random() * SUP_NAMES.length)];
        ctx.supplierName = name;
        if (!ctx.senderName) ctx.senderName = name;
      } else if (tpl.senderType === 'bank') {
        ctx.bankName = 'BNB Mariba';
        if (!ctx.senderName) ctx.senderName = 'Service relation client';
      }

      get().pushMessageFromTemplate(tpl.id, ctx, { isNarrative: true });
      set({ lastRandomMessageDay: day });
    }
  },

  getUnreadCount: () => (get().inbox || []).filter(m => !m.read).length,
});
